import { memo, useCallback, useState } from 'react';

import { type Board, type Library } from '@air/api/types';
import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type ItemPickerBoardSelectParams } from '~/components/ItemPicker/itemPickerTypes';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';

const BOARD_ROW_BASE_INDENT = 8;
const BOARD_ROW_DEPTH_INDENT = 12;

export type ItemPickerSidebarBoardRowProps = {
  activeBoardId?: Board['id'];
  board: Board;
  depth: number;
  libraryId?: Library['id'];
  onSelectBoard: (params: ItemPickerBoardSelectParams) => void;
};

export const ItemPickerSidebarBoardRow = memo(
  ({ activeBoardId, board, depth, libraryId, onSelectBoard }: ItemPickerSidebarBoardRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasSubBoards = (board.directDescendantBoardCount ?? 0) > 0;
    const isActive = activeBoardId === board.id;

    const { data: subBoards } = useSubBoardsList({
      boardId: board.id,
      canFetchData: isExpanded,
      libraryId,
    });

    const handleToggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const handleSelect = useCallback(() => {
      onSelectBoard({ board, libraryId });
    }, [board, libraryId, onSelectBoard]);

    return (
      <div>
        <div
          className={cn(
            'flex h-8 items-center rounded-4 font-medium text-14 transition-colors',
            isActive
              ? 'ads:bg-subtle bg-gray-3 ads:text-primary text-gray-12'
              : 'ads:text-secondary text-gray-11 ads:hover:bg-hover-subtle hover:bg-gray-3',
          )}
          data-testid="ITEM_PICKER_SIDEBAR_BOARD_ROW"
          style={{ paddingLeft: BOARD_ROW_BASE_INDENT + depth * BOARD_ROW_DEPTH_INDENT }}
        >
          {hasSubBoards ? (
            <button
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${board.title}`}
              className="flex size-5 shrink-0 items-center justify-center rounded-2 ads:text-icon-subtle text-gray-9 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
              onClick={handleToggleExpanded}
              type="button"
            >
              <ChevronRightIcon className={cn('transition-transform', isExpanded && 'rotate-90')} size="xs" />
            </button>
          ) : (
            <div aria-hidden="true" className="size-5 shrink-0" />
          )}
          <button
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'flex h-full grow items-center truncate rounded-4 px-1 text-left outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset',
              isActive && 'font-semibold',
            )}
            onClick={handleSelect}
            type="button"
          >
            <span className="truncate">{board.title}</span>
          </button>
        </div>

        {isExpanded &&
          subBoards.map((subBoard) => (
            <ItemPickerSidebarBoardRow
              activeBoardId={activeBoardId}
              board={subBoard}
              depth={depth + 1}
              key={subBoard.id}
              libraryId={libraryId}
              onSelectBoard={onSelectBoard}
            />
          ))}
      </div>
    );
  },
);

ItemPickerSidebarBoardRow.displayName = 'ItemPickerSidebarBoardRow';
